import { z } from 'zod';
export var LayoutBlockType;
(function (LayoutBlockType) {
    LayoutBlockType["HERO"] = "hero";
    LayoutBlockType["TEXT"] = "text";
    LayoutBlockType["IMAGE_FULL"] = "image_full";
    LayoutBlockType["IMAGE_GRID"] = "image_grid";
    LayoutBlockType["VIDEO"] = "video";
    LayoutBlockType["COLUMNS_2"] = "columns_2";
    LayoutBlockType["SPACER"] = "spacer";
    LayoutBlockType["CASE_INFO"] = "case_info";
    LayoutBlockType["MOTION"] = "motion";
})(LayoutBlockType || (LayoutBlockType = {}));
// Schema for base64 image data (thumbnail + full)
export const ImageDataSchema = z.object({
    thumbnail: z.string(), // base64, ~30KB for list views
    full: z.string(), // base64, ~150KB for detail views
    width: z.number(),
    height: z.number(),
    alt: z.string().optional(),
});
// Schema for CDN-backed high quality images
// Used across all block types that support CDN uploads
export const CDNImageDataSchema = z.object({
    url: z.string(), // Supabase URL for public display (high quality)
    preview: z.string(), // base64 ~30KB for admin preview
    width: z.number(),
    height: z.number(),
    alt: z.string().optional(),
});
// Alias for backward compatibility
export const HeroImageDataSchema = CDNImageDataSchema;
// Union schema for images that support both CDN and legacy formats
// Use this for all image fields that need backward compatibility
export const ImageUnionSchema = z.union([CDNImageDataSchema, ImageDataSchema]);
// Schema for video data (used in columns)
export const VideoDataSchema = z.object({
    url: z.string(), // YouTube, Vimeo, or direct video URL
    poster: ImageUnionSchema.optional(), // Thumbnail image (CDN or legacy)
    autoplay: z.boolean().optional().default(false),
    loop: z.boolean().optional().default(false),
    muted: z.boolean().optional().default(false),
});
// Schema for motion data (short looping muted video, always autoplay)
export const MotionDataSchema = z.object({
    url: z.string(),
    poster: ImageUnionSchema.optional(),
    width: z.number().optional(),
    height: z.number().optional(),
    alt: z.string().optional(),
});
// Base schema for all blocks
const BaseBlockSchema = z.object({
    id: z.string(),
    type: z.nativeEnum(LayoutBlockType),
});
// 1. Hero Block
// Supports both legacy ImageData (base64) and new HeroImageData (CDN URL)
export const HeroBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.HERO),
    content: z.object({
        title: z.string().optional(),
        subtitle: z.string().optional(),
        // Union: new HeroImageData (url+preview) or legacy ImageData (thumbnail+full)
        image: z.union([HeroImageDataSchema, ImageDataSchema]).optional(),
        // Optional mobile-specific image shown only on small screens
        mobileImage: z.union([HeroImageDataSchema, ImageDataSchema]).optional(),
        videoUrl: z.string().url().optional(),
    }),
    settings: z
        .object({
        height: z.enum(['small', 'medium', 'large', 'full']).default('medium'),
        alignment: z.enum(['left', 'center', 'right']).default('center'),
        overlayOpacity: z.number().min(0).max(100).default(0),
    })
        .optional(),
});
// 2. Text Block
export const TextBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.TEXT),
    content: z.object({
        text: z.string(), // HTML or Markdown
    }),
    settings: z
        .object({
        maxWidth: z.enum(['small', 'medium', 'large', 'full']).default('medium'),
        alignment: z.enum(['left', 'center', 'right']).default('left'),
    })
        .optional(),
});
// 3. Full Width Image
// Supports both CDN (url+preview) and legacy (thumbnail+full) image formats
export const ImageFullBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.IMAGE_FULL),
    content: z.object({
        image: ImageUnionSchema.optional(),
        caption: z.string().optional(),
    }),
    settings: z
        .object({
    // No settings for now as aspect ratio is fixed
    })
        .optional(),
});
// 4. Image Grid (Gallery)
// Supports both CDN and legacy image formats in the array
export const ImageGridBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.IMAGE_GRID),
    content: z.object({
        images: z.array(ImageUnionSchema),
    }),
    settings: z
        .object({
        columns: z.number().min(2).max(4).default(2),
        gap: z.enum(['small', 'medium', 'large']).default('medium'),
    })
        .optional(),
});
// 5. Video
// Supports both CDN and legacy image formats for posterImage
export const VideoBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.VIDEO),
    content: z.object({
        url: z.string().url(), // YouTube, Vimeo, or direct link
        posterImage: ImageUnionSchema.optional(),
    }),
    settings: z
        .object({
        autoplay: z.boolean().default(false),
        loop: z.boolean().default(false),
        muted: z.boolean().default(false),
    })
        .optional(),
});
// 6. Motion Block (looping muted autoplay video)
export const MotionBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.MOTION),
    content: z.object({
        motion: MotionDataSchema,
        caption: z.string().optional(),
    }),
    settings: z
        .object({
        aspectRatio: z
            .enum(['auto', '16:9', '4:3', '1:1', '9:16'])
            .default('auto'),
        objectFit: z.enum(['cover', 'contain']).default('cover'),
        borderRadius: z.boolean().default(true),
    })
        .optional(),
});
// 7. Two Columns
// Supports CDN images, legacy images, and video data for each column
export const Columns2BlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.COLUMNS_2),
    content: z.object({
        left: z.union([ImageUnionSchema, VideoDataSchema, MotionDataSchema]), // CDN/legacy image, video, or motion
        right: z.union([ImageUnionSchema, VideoDataSchema, MotionDataSchema]),
        leftType: z.enum(['thumbnail', 'image', 'videos', 'motion']),
        rightType: z.enum(['thumbnail', 'image', 'videos', 'motion']),
    }),
    settings: z
        .object({
        ratio: z.enum(['1:1', '1:2', '2:1']).default('1:1'),
    })
        .optional(),
});
// 7. Spacer
export const SpacerBlockSchema = BaseBlockSchema.extend({
    type: z.literal(LayoutBlockType.SPACER),
    settings: z.object({
        height: z.enum(['small', 'medium', 'large', 'xlarge']).default('medium'),
    }),
});
// Union of all blocks
export const LayoutBlockSchema = z.discriminatedUnion('type', [
    HeroBlockSchema,
    TextBlockSchema,
    ImageFullBlockSchema,
    ImageGridBlockSchema,
    VideoBlockSchema,
    MotionBlockSchema,
    Columns2BlockSchema,
    SpacerBlockSchema,
    // 8. Case Info
    z.object({
        id: z.string(),
        type: z.literal(LayoutBlockType.CASE_INFO),
        content: z.object({
            client: z.string().optional(),
            description: z.string().optional(),
            roles: z.array(z.string()).default([]),
            sectors: z.array(z.string()).default([]),
        }),
        settings: z
            .object({
            layout: z.enum(['default', 'three-column']).default('default'),
        })
            .optional(),
    }),
]);
// The full layout is just an array of blocks
export const ProjectLayoutSchema = z.array(LayoutBlockSchema);
